import { Injectable } from '@angular/core';
import { BehaviorSubject, Observable } from 'rxjs';

export type OverlayKey = 'imprint' | 'privacy';
export type OverlayVariant = 'light' | 'dark';
export type OverlayPhase = 'idle' | 'expanding' | 'expanded' | 'shrinking';

export interface OverlayRuntime {
  /** Status-Flag: Overlay ist gerade in einem Übergang (expand/shrink) */
  busy: boolean;

  /** "Mounted": Overlay existiert / pointer-events aktiv */
  visible: boolean;

  /** Animations-/Lebenszyklusstatus */
  phase: OverlayPhase;

  /** Aktueller Theme-Mode */
  darkMode: boolean;

  /** Welche Variante (light/dark) ist oben / aktiv? */
  activeVariant: OverlayVariant;

  /** DOM IDs (optional, aber praktisch fürs Debug) */
  ids: {
    light: string;
    dark: string;
  };

  wipeNonce: number; // erhöht sich bei Theme-Toggle, triggert Wipe
  wipeSeenNonce: number; // zuletzt “verarbeitete” Nonce


  /** Debug/Tracing */
  lastAction:
  | 'init'
  | 'open'
  | 'close'
  | 'toggle'
  | 'setDarkMode'
  | 'animEnd'
  | 'force';
  lastChangedAt: number;
}

export interface OverlayState {
  overlays: Record<OverlayKey, OverlayRuntime>;
}

function now(): number {
  return Date.now();
}

function makeOverlayRuntime(key: OverlayKey): OverlayRuntime {
  const lightId = key === 'imprint' ? 'imprintOverlay' : 'privacyPolicyOverlay';
  const darkId =
    key === 'imprint'
      ? 'dark-container-imprintOverlay'
      : 'dark-container-privacyPolicyOverlay';

  return {
    busy: false,
    visible: false,
    phase: 'idle',
    darkMode: false,
    activeVariant: 'light',
    ids: { light: lightId, dark: darkId },
    lastAction: 'init',
    lastChangedAt: now(),
    wipeNonce: 0,
    wipeSeenNonce: 0
  };
}

export const initialOverlayState: OverlayState = {
  overlays: {
    imprint: makeOverlayRuntime('imprint'),
    privacy: makeOverlayRuntime('privacy'),
  },
};

@Injectable({ providedIn: 'root' })
export class OverlayStateService {
  private readonly _state$ = new BehaviorSubject<OverlayState>(initialOverlayState);
  readonly state$: Observable<OverlayState> = this._state$.asObservable();

  /** Snapshot (synchron) */
  get snapshot(): OverlayState {
    return this._state$.value;
  }

  /** Einzel-overlay snapshot */
  snapshotOf(key: OverlayKey): OverlayRuntime {
    return this.snapshot.overlays[key];
  }

  // ---------------------------------------------------------------------------
  // Theme / DarkMode
  // ---------------------------------------------------------------------------
  /**
   * Setzt global für ALLE Overlays den DarkMode.
   * activeVariant wird automatisch so gesetzt, dass die "aktive" Variante oben liegt.
   */
  setDarkMode(isDarkMode: boolean, triggerWipe: boolean): void {
    const s = this.snapshot;
    const nextVariant = isDarkMode ? 'dark' : 'light';

    const bump = (ov: OverlayRuntime) =>
      triggerWipe && ov.visible ? ov.wipeNonce + 1 : ov.wipeNonce;

    this._state$.next({
      overlays: {
        imprint: {
          ...s.overlays.imprint,
          darkMode: isDarkMode,
          activeVariant: nextVariant,
          wipeNonce: bump(s.overlays.imprint),
          lastAction: 'setDarkMode',
          lastChangedAt: now(),
        },
        privacy: {
          ...s.overlays.privacy,
          darkMode: isDarkMode,
          activeVariant: nextVariant,
          wipeNonce: bump(s.overlays.privacy),
          lastAction: 'setDarkMode',
          lastChangedAt: now(),
        },
      },
    });

    this.logSnapshot('setDarkMode', 'imprint');
  }




  // ---------------------------------------------------------------------------
  // Visibility + Phase API (FIXED)
  // ---------------------------------------------------------------------------
  /**
   * FIX: Open blockt NICHT mehr auf busy.
   * Open darf shrinking/idle überstimmen.
   */
  open(key: OverlayKey): boolean {
    this.patch(key, {
      busy: true,
      visible: true,
      phase: 'expanding',
      lastAction: 'open',
    });
    return true;
  }


  /**
   * FIX: Close blockt NICHT mehr auf busy.
   * Close darf auch während expanding greifen.
   * visible bleibt true, bis shrink animEnd kommt.
   */
  close(key: OverlayKey): boolean {
    const ov = this.snapshotOf(key);
    if (!ov.visible) return true;

    // ✅ falls ein Wipe pending ist, nicht ins nächste Open tragen
    const wipeSeenNonce = ov.wipeNonce; // ack alles bis jetzt

    this.patch(key, {
      busy: true,
      phase: 'shrinking',
      wipeSeenNonce,          // ✅ reset pending wipe
      lastAction: 'close',
    });
    this.logSnapshot(key);
    return true;
  }

  /**
   * FIX: Toggle entscheidet nach phase (nicht nur visible),
   * damit expanding/shrinking sauber überstimmt werden können.
   */
  toggle(key: OverlayKey): boolean {
    const ov = this.snapshotOf(key);

    console.log(ov);


    if (ov.phase === 'expanding' || ov.phase === 'expanded') {
      //this.patch(key, { lastAction: 'toggle' });
      return this.close(key);
    }

    // idle oder shrinking
    this.patch(key, { lastAction: 'toggle' });
    return this.openExclusive(key);
  }

  /**
   * Muss aus deiner AppComponent/OverlayShell beim animationend aufgerufen werden.
   * - expand end => phase=expanded, busy=false
   * - shrink end => phase=idle, visible=false, busy=false
   */
  onAnimationEnd(key: OverlayKey, kind: 'expand' | 'shrink'): void {
    if (kind === 'expand') {
      this.patch(key, {
        phase: 'expanded',
        busy: false,
        lastAction: 'animEnd',
      });
      return;
    }

    // shrink end
    this.patch(key, {
      phase: 'idle',
      visible: false,
      busy: false,
      lastAction: 'animEnd',
    });
  }

  /**
   * Direkt sichtbarkeit setzen (ohne Animation). Eher für Debug/Recovery.
   */
  setVisibleInstant(key: OverlayKey, visible: boolean): void {
    this.patch(key, {
      visible,
      phase: visible ? 'expanded' : 'idle',
      busy: false,
      lastAction: 'force',
    });
  }

  /**
   * Für deinen Wipe-Mechanismus: explizit entscheiden,
   * welche Variante oben liegen soll.
   */
  setActiveVariant(key: OverlayKey, variant: OverlayVariant): void {
    this.patch(key, {
      activeVariant: variant,
      lastAction: 'force',
    });
  }

  // ---------------------------------------------------------------------------
  // Optional: "only one overlay at a time"
  // ---------------------------------------------------------------------------
  /**
   * Wenn du willst: beim Öffnen eines Overlays das andere schließen.
   * (Nicht automatisch aktiv – nur bei Bedarf aufrufen.)
   */
  openExclusive(key: OverlayKey): boolean {
    const other: OverlayKey = key === 'imprint' ? 'privacy' : 'imprint';
    this.close(other);
    return this.open(key);
  }

  ackWipe(key: OverlayKey): void {
    const ov = this.snapshotOf(key);
    if (ov.wipeSeenNonce === ov.wipeNonce) return;

    this.patch(key, {
      wipeSeenNonce: ov.wipeNonce,
      lastAction: 'force',
    });
  }


  // ---------------------------------------------------------------------------
  // Internals
  // ---------------------------------------------------------------------------
  private patch(key: OverlayKey, patch: Partial<OverlayRuntime>): void {
    const s = this.snapshot;
    const current = s.overlays[key];

    const next: OverlayRuntime = {
      ...current,
      ...patch,
      lastAction: patch.lastAction ?? current.lastAction,
      lastChangedAt: now(),
    };

    this._state$.next({
      overlays: {
        ...s.overlays,
        [key]: next,
      },
    });
  }

  // ---------------------------------------------------------------------------
  // Debug Logging
  // ---------------------------------------------------------------------------
  logSnapshot(label: string, key?: OverlayKey, opts: { collapsed?: boolean } = {}): void {
    const state = this.snapshot;
    const collapsed = opts.collapsed ?? true;

    const group = (title: string) => {
      if (collapsed) console.groupCollapsed?.(title);
      else console.group?.(title);
    };

    if (key) {
      const ov = state.overlays[key];
      if (!ov) {
        console.warn(`[OverlayState] ${label} – unknown key`, key);
        return;
      }

      group(`[OverlayState] ${label} → ${key}`);

      console.table({
        visible: ov.visible,
        busy: ov.busy,
        phase: ov.phase,
        darkMode: ov.darkMode,
        activeVariant: ov.activeVariant,
        lastAction: ov.lastAction,
        lastChangedAt: new Date(ov.lastChangedAt).toISOString(),
        wipeNonce: ov.wipeNonce,
        wipeSeenNonce: ov.wipeSeenNonce
      });

      console.log('ids:', ov.ids);
      console.groupEnd?.();
      return;
    }

    group(`[OverlayState] ${label} → FULL SNAPSHOT`);

    (Object.entries(state.overlays) as Array<[OverlayKey, OverlayRuntime]>).forEach(([k, ov]) => {
      console.groupCollapsed?.(k);
      console.table({
        visible: ov.visible,
        busy: ov.busy,
        phase: ov.phase,
        darkMode: ov.darkMode,
        activeVariant: ov.activeVariant,
        lastAction: ov.lastAction,
        lastChangedAt: new Date(ov.lastChangedAt).toISOString(),
        wipeNonce: ov.wipeNonce,
        wipeSeenNonce: ov.wipeSeenNonce
      });
      console.groupEnd?.();
    });

    console.groupEnd?.();
  }

  logToggle(key: OverlayKey): void {
    this.logSnapshot('TOGGLE', key);
  }

  logOpen(key: OverlayKey): void {
    this.logSnapshot('OPEN', key);
  }

  logClose(key: OverlayKey): void {
    this.logSnapshot('CLOSE', key);
  }
}
